import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";

interface AssociationFieldsProps {
  form: UseFormReturn<any>;
  variants: any[];
  factories: any[];
  loadingVariants: boolean;
  loadingFactories: boolean;
}

export function AssociationFields({
  form,
  variants,
  factories,
  loadingVariants,
  loadingFactories
}: AssociationFieldsProps) {
  const selectedVariantIds = form.watch("variant") || [];
  const selectedFactoryNames = form.watch("factories") || [];
  const isDefault = form.watch("is_default");
  const variantGroups = form.watch("variant_group") || {};

  const handleVariantChange = (variantId: string, checked: boolean) => {
    const updatedVariants = checked
      ? [...selectedVariantIds, variantId]
      : selectedVariantIds.filter((id: string) => id !== variantId);

    form.setValue("variant", updatedVariants, {
      shouldValidate: true,
      shouldDirty: true
    });

    // Remove group selection when variant is unchecked
    if (!checked && variantGroups[variantId]) {
      const updatedGroups = { ...variantGroups };
      delete updatedGroups[variantId];
      form.setValue("variant_group", updatedGroups, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };

  const handleGroupChange = (variantId: string, groupNumber: string) => {
    const updatedGroups = {
      ...variantGroups,
      [variantId]: parseInt(groupNumber)
    };
    form.setValue("variant_group", updatedGroups, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleFactoryChange = (factoryName: string, checked: boolean) => {
    const updatedFactories = checked
      ? [...selectedFactoryNames, factoryName]
      : selectedFactoryNames.filter((name: string) => name !== factoryName);

    form.setValue("factories", updatedFactories, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

    const handleDefaultChange = (checked: boolean) => {
      form.setValue("is_default", checked, {
        shouldValidate: true,
        shouldDirty: true
      });

      if (checked) {
        // Select all factory names when "default" is checked
        const allFactories = factories.map((factory) => factory.name);
        form.setValue("factories", allFactories, {
          shouldValidate: true,
          shouldDirty: true
        });
      } else {
        // If unchecked, clear factories (or keep previous selection depending on logic)
        form.setValue("factories", [], {
          shouldValidate: true,
          shouldDirty: true
        });
      }
    };


  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Variants Section */}
      <FormField
        control={form.control}
        name="variant"
        render={() => (
          <FormItem>
            <FormLabel>Associated Variants</FormLabel>
            <FormControl>
              <div className="border rounded-md p-1">
                <ScrollArea className="h-[150px]">
                  <div className="p-3 space-y-2">
                    {loadingVariants ? (
                      <p className="text-sm text-muted-foreground">Loading variants...</p>
                    ) : variants.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No variants available</p>
                    ) : (
                      variants.map((variant) => {
                        const variantGroupsArray = Array.isArray(variant.variant_group) 
                          ? variant.variant_group 
                          : [];
                        const isVariantSelected = selectedVariantIds.includes(variant.id);
                        
                        return (
                          <div key={variant.id} className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`variant-${variant.id}`}
                                checked={isVariantSelected}
                                onCheckedChange={(checked) =>
                                  handleVariantChange(variant.id, !!checked)
                                }
                              />
                              <label
                                htmlFor={`variant-${variant.id}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              >
                                {variant.name}
                              </label>
                            </div>
                            
                            {isVariantSelected && variantGroupsArray.length > 0 && (
                              <div className="ml-6">
                                <Select
                                  value={variantGroups[variant.id]?.toString() || ""}
                                  onValueChange={(value) => handleGroupChange(variant.id, value)}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select group" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {variantGroupsArray.map((group: any) => (
                                      <SelectItem 
                                        key={group.group_number} 
                                        value={group.group_number.toString()}
                                      >
                                        Group {group.group_number} - {group.group_name || 'Unnamed'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Factories Section */}
      <FormField
        control={form.control}
        name="factories"
        render={() => (
          <FormItem className="asfac" style={{ marginTop: "-22px" }}>
            <FormLabel>Associated Factories</FormLabel>

            {/* Default Checkbox */}
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
              <FormControl>
                <Checkbox
                  checked={isDefault}
                  onCheckedChange={(checked) => handleDefaultChange(!!checked)}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Available in all factories</FormLabel>
              </div>
            </FormItem>

            {/* Factories list always visible but faded/disabled if isDefault */}
            <FormControl>
              <div
                className={`border rounded-md p-1 transition-opacity ${
                  isDefault ? "opacity-50 pointer-events-none" : "opacity-100"
                }`}
              >
                <ScrollArea className="h-[150px]">
                  <div className="p-3 space-y-2">
                    {loadingFactories ? (
                      <p className="text-sm text-muted-foreground">
                        Loading factories...
                      </p>
                    ) : factories.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No factories available
                      </p>
                    ) : (
                      factories.map((factory) => (
                        <div
                          key={factory.id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`factory-${factory.id}`}
                            checked={selectedFactoryNames.includes(factory.name)}
                            onCheckedChange={(checked) =>
                              handleFactoryChange(factory.name, !!checked)
                            }
                            disabled={isDefault} // disable checkbox input
                          />
                          <label
                            htmlFor={`factory-${factory.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {factory.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            </FormControl>

            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
